import Component from '@glimmer/component'
import { action } from '@ember/object'
import { isEmpty } from '@ember/utils'
import { service } from '@ember/service'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import { eq, or } from 'ember-truth-helpers'
import { fn, concat, hash } from '@ember/helper'
import { optional } from 'teacher-ui-client/helpers/optional'
import { Changeset } from 'ember-changeset'
import { task } from 'ember-concurrency'
import { Product } from 'teacher-ui-client/utils/products'
import type Student from 'teacher-ui-client/models/student'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { IntlService } from 'ember-intl'
import type { BufferedChangeset } from 'ember-changeset/types'
import type { BaseOption } from 'teacher-ui-client/progress-handler/utils'
import type { Log } from '@blakeelearning/log'
import { join } from 'teacher-ui-client/utils/uri'
import config from 'teacher-ui-client/config/environment'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { UiButtonState } from 'teacher-ui-client/components/ui-button/state'
import { EditProgressFastPhonics } from 'teacher-ui-client/components/form/edit-progress/fast-phonics'
import { EditProgressMathseedsPrime } from 'teacher-ui-client/components/form/edit-progress/mathseeds-prime'
import { EditProgressMathseeds } from 'teacher-ui-client/components/form/edit-progress/mathseeds'
import { EditProgressWritingLegends } from 'teacher-ui-client/components/form/edit-progress/writing-legends'
import { EditProgressReadingEggspress } from 'teacher-ui-client/components/form/edit-progress/reading-eggspress'
import { EditProgressReadingEggs } from 'teacher-ui-client/components/form/edit-progress/reading-eggs'
import { teacherEventClick } from 'teacher-ui-client/modifiers/teacher-event'
import type SessionService from 'teacher-ui-client/services/session'

interface Args {
  product: Product
  editStudents: Student[]
  onSuccess?: (studentProgresses?: Record<PropertyKey, unknown>[]) => unknown
  onCancel?: () => unknown
}

interface Signature {
  Args: Args
}

/**
 * Displays a product tab selection, manages a progress changeset and submission of the changeset. The option selection
 * and changeset editing is conducted by the individual product progress sub-components.
 */
export class FormEditProgress extends Component<Signature> {
  @service intl!: IntlService

  @service authToken!: AuthToken

  @service studentProgress!: StudentProgressService

  @service flashQueue!: FlashQueueService

  @service log!: Log

  @service session!: SessionService

  progressChangeset = Changeset({})

  get isReadingEggs() {
    return this.args.product === Product.RE
  }

  get isReadingEggspress() {
    return this.args.product === Product.REX
  }

  get isFastPhonics() {
    return this.args.product === Product.FP
  }

  get isMathseeds() {
    return this.args.product === Product.MS
  }

  get isMathseedsPrime() {
    return this.args.product === Product.MSP
  }

  get isWritingLegends() {
    return this.args.product === Product.WL
  }

  /**
   * When editing a single student, the form will prefill dropdown values and show the students' name.
   */
  get isSingleStudent() {
    return this.args.editStudents.length === 1
  }

  get firstStudent() {
    return this.args.editStudents[0]
  }

  get firstStudentProgression() {
    return this.isSingleStudent && this.firstStudent.id
      ? this.studentProgress.allProgressFor(this.firstStudent.id)
      : undefined
  }

  @action
  onSubmit(progressChangeset: BufferedChangeset, event: Event) {
    event.preventDefault()

    const progressChanges = progressChangeset?.changes
    if (isEmpty(progressChanges)) return

    const studentIds = this.args.editStudents.map((s) => s.id)
    const serializedProgresses = progressChanges.map(({ value }) => (value as BaseOption).serializedOption)
    const studentProgresses = serializedProgresses.map((serialized) => ({ ...serialized, student_ids: studentIds }))

    void this.setProgressesTask.perform(studentProgresses)
  }

  setProgressesTask = task({ drop: true }, async (studentProgresses: Record<PropertyKey, unknown>[]) => {
    try {
      await this.setStudentProgressesGravity(studentProgresses)
      const message = this.isSingleStudent
        ? this.intl.t('manage.manageProgress.editProgress.success', { name: this.firstStudent.fullName })
        : this.intl.t('manage.multipleProgressesSet.success')

      this.flashQueue.addSuccess({ subtitle: message })

      this.args.onSuccess?.(studentProgresses)
    } catch (error: unknown) {
      this.log.error('failed to set student progresses', error ?? undefined)
      const message = this.isSingleStudent
        ? this.intl.t('manage.manageProgress.editProgress.error', { name: this.firstStudent.fullName })
        : this.intl.t('manage.multipleProgressesSet.error')

      this.flashQueue.addFail({ subtitle: message })
    }
  })

  async setStudentProgressesGravity(studentProgresses: Record<PropertyKey, unknown>[] = []) {
    if (isEmpty(studentProgresses)) return Promise.resolve()

    const data = { student_progresses: studentProgresses }
    const url = join(config.gravityV3Url, 'teacher-ui/progression/api-set').href
    const response = await fetch(url, {
      body: JSON.stringify(data),
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: this.authToken.token ?? '',
      },
    })
    if (!response.ok) throw new Error(await response.text())
  }

  <template>
    <form {{on "submit" (fn this.onSubmit this.progressChangeset)}} data-test-form-edit-progress>
      <div class="mx-24 my-8 space-y-3">
        <p data-test-warning-message class="overflow-hidden text-ellipsis">
          {{#if this.isSingleStudent}}
            {{t
              "manage.manageProgress.bulkActions.editProgress.warningMessagePersonalised"
              name=this.firstStudent.fullName
              htmlSafe=true
            }}
          {{else}}
            {{t
              "manage.manageProgress.bulkActions.editProgress.warningMessage"
              count=@editStudents.length
              htmlSafe=true
            }}
          {{/if}}
        </p>
        <hr />
        <h4 data-test-product-name>{{t (concat "products." @product)}}</h4>
        {{#if this.isReadingEggs}}
          <EditProgressReadingEggs
            @progressChangeset={{this.progressChangeset}}
            @studentProgression={{if this.isSingleStudent this.firstStudentProgression}}
          />
        {{else if this.isReadingEggspress}}
          <EditProgressReadingEggspress
            @progressChangeset={{this.progressChangeset}}
            @studentProgression={{if this.isSingleStudent this.firstStudentProgression}}
          />
        {{else if this.isFastPhonics}}
          <EditProgressFastPhonics
            @progressChangeset={{this.progressChangeset}}
            @studentProgression={{if this.isSingleStudent this.firstStudentProgression}}
          />
        {{else if this.isMathseeds}}
          <EditProgressMathseeds
            @progressChangeset={{this.progressChangeset}}
            @studentProgression={{if this.isSingleStudent this.firstStudentProgression}}
          />
        {{else if this.isMathseedsPrime}}
          <EditProgressMathseedsPrime
            @progressChangeset={{this.progressChangeset}}
            @studentProgression={{if this.isSingleStudent this.firstStudentProgression}}
          />
        {{else if this.isWritingLegends}}
          <EditProgressWritingLegends
            @progressChangeset={{this.progressChangeset}}
            @studentProgression={{if this.isSingleStudent this.firstStudentProgression}}
          />
        {{/if}}
      </div>
      <div class="border-dusty-black-50 border-t p-2 text-right">
        <UiButton type="reset" {{on "click" (optional @onCancel)}}>
          {{t "manage.manageProgress.bulkActions.cancel"}}
        </UiButton>
        <UiButton
          type="submit"
          @appearance={{ButtonAppearance.regular}}
          @disabled={{or this.progressChangeset.isPristine this.setProgressesTask.isRunning}}
          {{teacherEventClick
            "manage-progress-edit"
            @product
            eventData=(hash
              operation=(if (eq @editStudents.length 1) "edit" "bulk-edit")
              studentCount=@editStudents.length
              teacherId=this.session.currentTeacher.id
              schoolId=this.session.currentSchool.id
            )
          }}
        >
          <UiButtonState @isLoading={{this.setProgressesTask.isRunning}}>
            {{t "manage.manageProgress.bulkActions.update"}}
          </UiButtonState>
        </UiButton>
      </div>
    </form>
  </template>
}

export default FormEditProgress
